import { MantineProvider } from "@mantine/core";
import { ModalsProvider } from '@mantine/modals';
import { Notifications } from "@mantine/notifications";
import { QueryClientProvider } from "@tanstack/react-query";
import React, { FC, useEffect, useMemo } from "react";
import ViewportProvider from "@/core/providers/viewport.provider";
import '@mantine/dropzone/styles.css';
import '@mantine/tiptap/styles.css';
import 'mantine-react-table/styles.css';
import { DatesProvider } from "@mantine/dates";
import 'dayjs/locale/en';
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { appTheme, appCssVariablesResolver } from "@/core/theme";
import { branding } from "@/core/theme/config";
import { ThemeProvider, useTheme } from "@/core/providers/theme.provider";
import { ThemeProvider as MuiThemeProvider, createTheme } from "@mui/material/styles";
import { queryClient } from "@/core/query/query.client";
import SSEProvider from "@/core/providers/sse.provider";
import { TaskNotificationProvider } from "@/core/providers/task-notification.provider";

interface AppProviderProps {
    children?: React.ReactNode;
}

const MantineProviderWithTheme: FC<{ children: React.ReactNode }> = ({ children }) => {
    const { colorScheme } = useTheme();

    const muiTheme = useMemo(
        () =>
            createTheme({
                palette: {
                    mode: colorScheme,
                    primary: {
                        main: branding.palette[5],
                        light: branding.palette[3],
                        dark: branding.palette[7],
                    },
                    secondary: {
                        main: branding.palette[6],
                    },
                },
            }),
        [colorScheme],
    );

    useEffect(() => {
        document.documentElement.setAttribute('data-mantine-color-scheme', colorScheme);
    }, [colorScheme]);

    return (
        <MuiThemeProvider theme={muiTheme}>
            <MantineProvider
                theme={appTheme}
                defaultColorScheme={colorScheme}
                cssVariablesResolver={appCssVariablesResolver}
            >
                {children}
            </MantineProvider>
        </MuiThemeProvider>
    );
};

const AppProvider: FC<AppProviderProps> = ({ children }) => {
    return (
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="en">
            <ThemeProvider>
                <MantineProviderWithTheme>
                    <DatesProvider settings={{ locale: 'en', firstDayOfWeek: 0, weekendDays: [0] }}>
                        <QueryClientProvider client={queryClient}>
                            <ModalsProvider>
                                <Notifications position="top-center" />
                                <SSEProvider>
                                    <TaskNotificationProvider>
                                        <ViewportProvider>
                                            {children}
                                        </ViewportProvider>
                                    </TaskNotificationProvider>
                                </SSEProvider>
                            </ModalsProvider>
                        </QueryClientProvider>
                    </DatesProvider>
                </MantineProviderWithTheme>
            </ThemeProvider>
        </LocalizationProvider>
    );
};

export default AppProvider;
